import React from "react";
import { Box, LinearProgress, Typography } from "@mui/material";
import snowflake from "../images_new/snowflake.svg";
import Colors from "./Colors";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TypographyComponent from "./TypographyComponent";
import TypographyComponentNormal from "./TypographyComponentNormal";

const LEVELS = [
  { points: 100, color: Colors.yellowflake, flake: "yellow flake" },
  { points: 300, color: Colors.orengeflake, flake: "orenge flake" },
  { points: 600, color: Colors.redflake, flake: "red flake" },
  { points: 1000, color: Colors.greenflake, flake: "green flake" },
];

const GameLevel = ({ points }) => {
  const getProgressPercentage = (currentPoints, fromPoints, toPoints) => {
    if (currentPoints < fromPoints) return 0;
    if (currentPoints >= toPoints) return 100;
    return ((currentPoints - fromPoints) / (toPoints - fromPoints)) * 100;
  };

  const achievedLevel =
    LEVELS.findLast((level) => points >= level.points) || LEVELS[0];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        justifyContent: "center",
        alignItems: "center",
        gap: { md: "16px", xs: "8px" },
        height: "100%",
        marginBottom: "16px",
      }}
    >
      <TypographyComponent
        sx={{ fontWeight: 400, fontSize: "28px", color: Colors.greyFont }}
      >
        Hello, {localStorage.getItem("PlayerName")}
      </TypographyComponent>
      <TypographyComponentNormal
        sx={{ fontWeight: 400, fontSize: { md: "21px", xs: "12px" } }}
      >
        Congrats , You have achieved {achievedLevel.flake}
      </TypographyComponentNormal>

      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="row"
        gap={"2px"}
        position="relative"
        marginTop={{ md: -2, xs: -2 }}
      >
        <Box display="flex" justifyContent="center" alignItems="center">
          <LinearProgress
            variant="determinate"
            value={getProgressPercentage(points, 0, LEVELS[0].points)}
            sx={{
              height: { md: 7, xs: 4 },
              borderRadius: 4,
              //  border: "1px solid",
              borderColor: LEVELS[0].color,
              background: LEVELS[0].color,
              width: { md: "150px", xs: "46px" },
              "& .MuiLinearProgress-bar": {
                borderRadius: 5,
                backgroundColor: LEVELS[0].color,
              },
            }}
          />
        </Box>

        {LEVELS.map((level, index) => (
          <React.Fragment key={index}>
            <Box display="flex" flexDirection="column" alignItems="center">
              {/* Level Circle */}
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                width={{ md: 36, xs: 16 }}
                height={{ md: 36, xs: 16 }}
                borderRadius="50%"
                bgcolor={level.color}
                marginTop={"18px"}
                //  marginBottom={2}
              >
                <img
                  src={snowflake}
                  alt="Snowflake"
                  style={{
                    width: "70%",
                    height: "70%",
                    filter: "brightness(0) invert(1)",
                  }}
                />
              </Box>

              {/* Level Points Label */}
              <Typography
                variant="caption"
                sx={{
                  fontSize: { md: "14px", xs: "10px" },
                  mt: 0.5,
                  fontWeight: 500,
                  color: points >= level.points ? level.color : "gray",
                }}
              >
                {level.points}
              </Typography>
            </Box>

            {/* Progress Bar Between Levels */}
            {index < LEVELS.length - 1 && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                position="relative"
                width={{ md: "150px", xs: "46px" }}
              >
                <LinearProgress
                  variant="determinate"
                  value={getProgressPercentage(
                    points,
                    LEVELS[index].points,
                    LEVELS[index + 1].points,
                  )}
                  sx={{
                    height: { md: 7, xs: 4 },
                    borderRadius: 4,
                    // border: "1px solid",
                    borderColor:
                      points >= level.points
                        ? LEVELS[index + 1].color
                        : Colors.gamelevelgrey,
                    background: Colors.gamelevelgrey,
                    width: "100%",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 5,
                      backgroundColor:
                        points >= level.points
                          ? LEVELS[index + 1].color
                          : Colors.gamelevelgrey,
                    },
                  }}
                />

                {/* Line connecting progress bar to the triangle */}
                {points >= LEVELS[index].points &&
                  points < LEVELS[index + 1].points && (
                    <>
                      <Box
                        position="absolute"
                        left={`calc(${getProgressPercentage(points, LEVELS[index].points, LEVELS[index + 1].points)}% - 1px)`}
                        top={{ md: "8px", xs: "6px" }}
                        height="32px"
                        width="1px"
                        bgcolor={LEVELS[index + 1].color}
                        zIndex={2}
                      />

                      {/* Triangle Indicator (Now pointing upward) */}
                      <Box
                        position="absolute"
                        left={`calc(${getProgressPercentage(points, LEVELS[index].points, LEVELS[index + 1].points)}% - 10px)`}
                        top={{ md: "34px", xs: "28px" }}
                        width="0"
                        height="0"
                        borderLeft="10px solid transparent"
                        borderRight="10px solid transparent"
                        borderBottom={`10px solid ${LEVELS[index + 1].color}`} // Now pointing UPWARD
                        zIndex={3}
                      />

                      {/* Circle Displaying Current Points */}
                      <Box
                        position="absolute"
                        left={{
                          md: `calc(${getProgressPercentage(points, LEVELS[index].points, LEVELS[index + 1].points)}% - 42px)`,
                          xs: `calc(${getProgressPercentage(points, LEVELS[index].points, LEVELS[index + 1].points)}% - 28px)`,
                        }}
                        top={{ md: "44px", xs: "36px" }}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        gap={0.5} // Added a small gap between icon and text
                        width={{ md: "70px", xs: "40px" }}
                        height={{ md: "28px", xs: "20px" }}
                        borderRadius="28px"
                        bgcolor={LEVELS[index + 1].color}
                        color="white"
                        zIndex={4}
                        paddingX={1} // Ensures better spacing
                      >
                        <EmojiEventsIcon
                          sx={{
                            color: "white",
                            fontSize: { md: "18px", xs: "12px" },
                          }}
                        />
                        <TypographyComponent
                          sx={{
                            color: "white",
                            fontSize: { md: "14px", xs: "10px" },
                            fontWeight: 500,
                          }}
                        >
                          {points}
                        </TypographyComponent>
                      </Box>
                    </>
                  )}
              </Box>
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default GameLevel;
